/**
 * /playlists — the library's Playlists view.
 *
 * The second of the library's two views (see components/playlists/LibraryTabs), so it
 * repeats the Library's header exactly rather than inventing a heading of its own:
 * this is the same screen showing a different thing, and a page that renamed itself
 * would read as somewhere else entirely.
 *
 * NOTHING IS PERSISTED YET. A playlist is lost on reload, because playlistsApi is a
 * mock — see its header for the endpoints and src/api/playlistsApi.ts for the honest
 * statement of what is missing. The feature itself is real: it holds the user's own
 * songs, in the order they chose, and playing one really does move the player through
 * them.
 */
import { useState } from 'react';
import { ListMusic, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Btn, LoadError, LoadSkeleton } from '../components/ui';
import { TextField } from '../components/forms';
import { LibraryTabs, PlaylistCard } from '../components/playlists';
import { StudioHeader } from '../components/studio';
import { UNKNOWN_API_ERROR_MESSAGE } from '../api/errors';
import { usePlayer, usePlaylists } from '../hooks';
import { queueMatches } from '../lib/playback';
import { isFirstLoad } from '../stores/loadState';
import { usePlaylistsStore } from '../stores/playlistsStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import { paths } from './paths';

export default function Playlists() {
  const navigate = useNavigate();
  const { playlists, songsIn, summaryFor, createPlaylist, playPlaylist } = usePlaylists();
  /*
    THE APP'S ONE PLAYER, read here so the cards can report their own state.

    This screen had no player state at all, which is why a card's Play button was a static
    glyph that restarted the queue. It reads the same store the transport, the mini player and
    the playlist page read — nothing local, and no second playback state. See the per-card
    comparison below.
  */
  const player = usePlayer();
  const setRenamePlaylistTarget = useUiStore((s) => s.setRenamePlaylistTarget);
  const setDeletePlaylistTarget = useUiStore((s) => s.setDeletePlaylistTarget);
  // The read state, from the store — see the same pair in routes/Library.tsx.
  const load = usePlaylistsStore((s) => s.load);
  const loadPlaylists = usePlaylistsStore((s) => s.loadPlaylists);

  /*
    The create field is route-local: it is a form draft that only matters while it is
    on screen, which is the same rule the composer's prompt and the rename dialog's
    field follow. Nothing outside this screen reads it.
  */
  const [name, setName] = useState('');
  const trimmed = name.trim();

  function create() {
    const playlist = createPlaylist(trimmed);
    if (!playlist) return;
    setName('');
  }

  return (
    <div>
      <StudioHeader title="Library" subtitle="Your tracks and playlists." />

      <LibraryTabs active="playlists" />

      {/* Create sits above the list rather than behind a dialog: on an empty screen it
          IS the screen, and on a full one it is still the only thing you come here to
          do that a row cannot do for you.

          A real <form>, so Enter in the name field creates the playlist — the same fix
          Login.tsx, NewProjectForm.tsx and Ticket.tsx carry, with the same note in all
          three: "a real <form> so Enter submits, which the prototype's div did not". One
          field and one button is the shape where that gesture is most expected, and this
          was a div, so typing a name and pressing Enter did nothing at all.
          preventDefault because there is nowhere to post to; `create` is the submission,
          and it already refuses an empty or duplicate name. */}
      <form
        className="lyx-playlist-create"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <TextField
            id="lyx-playlist-name"
            value={name}
            onChange={setName}
            placeholder="New playlist name"
            ariaLabel="New playlist name"
            style={{ height: 34, padding: '0 10px' }}
          />
        </div>
        <Btn type="submit" disabled={!trimmed} style={{ height: 34, flexShrink: 0 }}>
          <Plus size={13} />
          Create playlist
        </Btn>
      </form>

      {/* The four read states. playlistsStore's own header names this screen as the
          motivating example: "the Playlists page would render 'Create your first playlist'
          over three playlists that are about to arrive. `isConfirmedEmpty` is the predicate
          that fixes it." This is that predicate reaching the screen it was written for.

          The create field above stays visible in every state — it is the one thing you can
          still do while a read is failing, and hiding it would make an outage look like a
          removed feature. */}
      {isFirstLoad(load, playlists.length > 0) ? (
        <LoadSkeleton rows={3} label="Loading your playlists…" />
      ) : load.status === 'error' && playlists.length === 0 ? (
        <LoadError
          message={load.error?.message ?? UNKNOWN_API_ERROR_MESSAGE}
          onRetry={loadPlaylists}
        />
      ) : playlists.length === 0 ? (
        // The Library's own empty state, in the same shape: small icon, one line, and
        // the action — which here is the field above, so this only explains what for.
        <div style={{ padding: '32px 0 20px', textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              margin: '0 auto 14px',
              borderRadius: 10,
              background: c.surface2,
              border: `1px solid ${c.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ListMusic size={17} color={c.text3} />
          </div>
          <p
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 14,
              margin: '0 0 4px',
            }}
          >
            No playlists yet
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
            Name one above, then add tracks from your library.
          </p>
        </div>
      ) : (
        <div className="lyx-song-grid">
          {playlists.map((playlist) => {
            /*
              WHETHER THE PLAYER IS ON THIS PLAYLIST, decided here rather than in the card.

              Compared as the whole resolved ORDER — see lib/playback.queueMatches for why
              "the loaded song is in this playlist" is the wrong test. Identical to what
              routes/Playlist.tsx computes for its header button, through the same helper, so
              the two screens cannot disagree about which playlist is playing.
            */
            const tracks = songsIn(playlist);
            const onThis =
              player.queueIndex >= 0 && queueMatches(player.queue, tracks.map((t) => t.id));

            return (
              <PlaylistCard
                key={playlist.id}
                playlist={playlist}
                songs={tracks}
                summary={summaryFor(playlist)}
                playing={onThis && !player.paused}
                active={onThis}
                onOpen={(p) => navigate(paths.playlist(p.id))}
                /*
                  Two gestures on one control, and which one a press means depends on the
                  state above.

                  Not this playlist: start it, from the top — what the button always did.

                  Already this playlist: pause or resume, and touch nothing else. Calling
                  playPlaylist instead would be wrong twice over — it would throw away your
                  position in the track, and because it rebuilds the queue from the
                  playlist's CURRENT contents it would silently discard the order you were
                  actually hearing. Pressing pause is not a request to re-open the playlist.

                  The same decision routes/Playlist.tsx makes for its own two Play controls.
                */
                onPlay={(p) =>
                  onThis && player.songId ? player.toggle(player.songId) : playPlaylist(p)
                }
                onRename={setRenamePlaylistTarget}
                onDelete={setDeletePlaylistTarget}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
